import { defineMessages } from "react-intl";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";

import { checkForCompaniesWithEmail, closeComposeModal } from "./common";

const messages = defineMessages({
  step01: {
    id: "guides.sendingEmailsViaRecordList.tour.step01",
    defaultMessage: "Sending Emails via List View",
  },
  step01Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step01.description",
    defaultMessage: "Best for when you want to email from a filtered or selected list of records.",
  },
  step02: {
    id: "guides.sendingEmailsViaRecordList.tour.step02",
    defaultMessage: "Records selection",
  },
  step02Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step02.description",
    defaultMessage:
      "Select the checkboxes next to the records you want to email. For simplicity, let's click Select All.",
  },
  step03: {
    id: "guides.sendingEmailsViaRecordList.tour.step03",
    defaultMessage: "Email Button",
  },
  step03Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step03.description",
    defaultMessage: "Click the Email button in the action bar.",
  },
  step04: {
    id: "guides.sendingEmailsViaRecordList.tour.step04",
    defaultMessage: "Email Button",
  },
  step04Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step04.description",
    defaultMessage: "Select Compose Email",
  },
  step05: {
    id: "guides.sendingEmailsViaRecordList.tour.step05",
    defaultMessage: "",
  },
  step05Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step05.description",
    defaultMessage: "",
  },
  step06: {
    id: "guides.sendingEmailsViaRecordList.tour.step06",
    defaultMessage: "No emails",
  },
  step06Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step06.description",
    defaultMessage:
      "In case some selected records have no emails, this modal will show up. Click Continue to proceed.",
  },
  step07: {
    id: "guides.sendingEmailsViaRecordList.tour.step07",
    defaultMessage: "Record type",
  },
  step07Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step07.description",
    defaultMessage:
      "Select who you want to email in the modal that pops up.{br}{br}Pick either option and click Continue.",
  },
  step08: {
    id: "guides.sendingEmailsViaRecordList.tour.step08",
    defaultMessage: "Compose your email",
  },
  step08Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step08.description",
    defaultMessage:
      "👤 Confirm the recipients in To{br}✉️ Add a Subject line{br}✍️ Write your Message - You can use the toolbar icons at the bottom left to insert a hyperlink, file, resource or dynamic variable.",
  },
  step09: {
    id: "guides.sendingEmailsViaRecordList.tour.step09",
    defaultMessage: "Send your email",
  },
  step09Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step09.description",
    defaultMessage:
      "If you have disabled the Open Activity Form setting, the button will show Send and Create.{br}If enabled or required fields exist, click Next to complete the activity form and then finalize.",
  },
  step10: {
    id: "guides.sendingEmailsViaRecordList.tour.step10",
    defaultMessage: "Tour Complete",
  },
  step10Description: {
    id: "guides.sendingEmailsViaRecordList.tour.step10.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const sendingViaRecordListTourConfiguration: TourDescriptor = {
  id: "mmc-sending-emails-via-record-list",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      onBefore: async ({ store }) => checkForCompaniesWithEmail(store),
      title: messages.step01,
    },
    {
      id: "selecting-1",
      description: messages.step02Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "right",
      selector: ".ag-header-cell[col-id=selection]",
      title: messages.step02,
      waitForEvent: {
        eventType: "click",
        selector: ".ag-header-cell[col-id=selection]",
      },
    },
    {
      id: "email-button-1",
      description: messages.step03Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-selection-button=bulk-email]",
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: "[data-selection-button=bulk-email]",
      },
    },
    {
      id: "email-button-2",
      description: messages.step04Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        return new Promise((resolve) => setTimeout(resolve, 100));
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "right",
      selector: "[data-email-button-item=compose]",
      title: messages.step04,
      waitForEvent: {
        eventType: "click",
        selector: "[data-email-button-item=compose]",
      },
    },
    {
      id: "email-button-3",
      onBefore: async () => {
        await new Promise<void>((resolve) =>
          waitForElement("[class*=mmc-js-modal-email]", resolve, {
            failureCallback: resolve,
            timeout: 1000,
          })
        );

        if (document.querySelector("[class*=mmc-js-modal-email-emails-missing]")) {
          return { action: "navigate", stepId: "email-modal-emails-missing" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-multiple-entities]")) {
          return { action: "navigate", stepId: "email-modal-multiple-entities" };
        }

        return { action: "navigate", stepId: "email-compose" };
      },
    },
    {
      id: "email-modal-emails-missing",
      description: messages.step06Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-multiple-entities",
      description: messages.step07Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      title: messages.step07,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      },
    },
    {
      id: "email-compose",
      description: messages.step08Description,
      title: messages.step08,
    },
    {
      id: "email-send",
      description: messages.step09Description,
      options: {
        showBackground: false,
      },
      selector: "[class*=EmailComposeModal_actionContainer] .ant-btn-primary",
      title: messages.step09,
    },
    {
      id: "completed",
      description: messages.step10Description,
      onAfter: closeComposeModal,
      title: messages.step10,
    },
  ],
};
